import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { notification } from 'antd';
import { AppContext } from 'App.context';
import { useNavigate } from 'react-router-dom';

export default function usePaperlessPushHook() {
  const { API } = useContext(ApiContext);
  const navigate = useNavigate();

  const {
    appState,
    appActions: { getSelectedHotel, translate },
  } = useContext(AppContext);
  const [state, setState] = useState({
    isLoading: false,
    dataSource: {
      items: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
      pageSize: 10,
    },
    originalWhitelists: [],
    filters: {},
    modifiedHotels: [],
    suscriptionEnabled: false,
    paperlessDevices: [],
    isPaperlessDevicesLoaded: false,
    selectedDeviceUUID: null,
    selectedDevice: null,
    deleteDeviceWarning: false,
    showEditDeviceModal: false,
  });

  useEffect(() => {
    actions.loadPapelessPushConfig();
  }, [state.dataSource.currentPage, state.filters]);

  useEffect(() => {
    const hasPrivileges = ['admin', 'owner'].includes(appState.role) || appState.superUser;
    if (hasPrivileges && !state.isPaperlessDevicesLoaded) {
      actions.loadPaperlessDevices();
    }
  }, [state.isPaperlessDevicesLoaded]);

  useEffect(() => {
    const loadPaperlessCredentials = async () => {
      for (const hotel of state.dataSource.items) {
        const hasCredentials = await API.checkApiPaperlessCrendetials(hotel);
        hotel.hasCredentials = hasCredentials.data;
      }
    };

    loadPaperlessCredentials();
  }, [state.dataSource]);

  const actions = {
    loadPapelessPushConfig: () => {
      updateState({ isLoading: true });

      API.getPaperlessPushConfig({
        page: state.dataSource?.currentPage,
        size: state.dataSource?.pageSize,
        filters: state.filters,
      })
        .then(res => {
          const whitelists = res.items.map(i => {
            return {
              hotelId: i.hotelId,
              list: i?.ipWhitelist?.split(',') || null,
            };
          });

          updateState({
            dataSource: res,
            isLoading: false,
            modifiedHotels: [],
            originalWhitelists: whitelists,
          });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
        });
    },

    onChange: filters => {
      const { pagination, where } = filters;
      const { currentPage, pageSize } = state.dataSource;

      const newPage = pagination?.page !== currentPage ? pagination.page : undefined;
      const newPageSize = pagination?.pageSize !== pageSize ? pagination.pageSize : undefined;
      const searchFilters = where ? { hotel: where.hotel } : undefined;

      updateState({
        dataSource: {
          ...state.dataSource,
          currentPage: newPage !== undefined ? newPage : currentPage,
          pageSize: newPageSize !== undefined ? newPageSize : pageSize,
        },
        filters: searchFilters,
      });
    },

    setPMSId: (hotelId, pmsId) => {
      const items = state.dataSource.items;
      const hotel = items.find(h => h.hotelId === hotelId);

      hotel.pmsId = pmsId;

      actions.setHotelAsModified(hotelId);
      updateState({
        dataSource: {
          ...state.dataSource,
          items,
        },
      });
    },

    setHotelWhitelist: (hotelId, whitelist) => {
      const items = state.dataSource.items;
      const hotel = items.find(h => h.hotelId === hotelId);

      hotel.ipWhitelist = whitelist;

      actions.setHotelAsModified(hotelId);
      updateState({
        dataSource: {
          ...state.dataSource,
          items,
        },
      });
    },

    setHotelAsModified: hotelId => {
      if (!state.modifiedHotels.includes(hotelId)) {
        const modifiedHotels = state.modifiedHotels;
        modifiedHotels.push(hotelId);

        updateState({ modifiedHotels });
      }
    },

    isValidIpList: whitelist => {
      const ipAddressRegex = /^(\d{1,3}\.){3}\d{1,3}(,\s*(\d{1,3}\.){3}\d{1,3})*$/;
      return whitelist === '' || whitelist === null || ipAddressRegex.test(whitelist);
    },

    onSaveClicked: () => {
      const hotels = state.dataSource.items.filter(item =>
        state.modifiedHotels.includes(item.hotelId)
      );

      const whitelists = actions.getIpConfigurations(hotels);
      const data = {
        hotels,
        whitelists,
      };

      if (actions.validateHotels(data.hotels)) {
        updateState({ isLoading: true });
        API.updatePaperlessPushConfig({ data })
          .then(_ => {
            notification.success({
              message: translate('paperlessPush.updateSuccess'),
            });
            updateState({ isLoading: false });
          })
          .catch(err => {
            notification.error({
              message: translate('paperlessPush.updateError'),
            });
            updateState({ isLoading: false });
          });
      }
    },

    getExampleLink: item => {
      return `https://paperless-push.civitfun.com/${item.apiKey}/${item.pmsId}/RESERVATION_ID/GUEST_ID`;
    },

    validateHotels: hotels => {
      let noErrors = true;
      for (const hotel of hotels) {
        if (!actions.isValidIpList(hotel.ipWhitelist)) {
          notification.error({
            message: translate('paperlessPush.errorWhitelist'),
          });

          noErrors = false;
        }
      }

      return noErrors;
    },

    goToAccountDetail: () => {
      navigate('/account-detail');
    },

    getApiPaperlessCrendetials: hotel => {
      updateState({ isLoading: true });
      API.getApiPaperlessCrendetials(hotel)
        .then(res => {
          notification.success({
            message: translate('paperlessPush.credentials'),
            description: (
              <>
                User: <b>{res.data.userName}</b> <br />
                Pass: <b>{res.data.password}</b>
              </>
            ),
            duration: 0,
            style: {
              width: 600,
            },
          });
          updateState({ isLoading: false });
        })
        .catch(err => {
          notification.error({
            message: err.message || response?.data?.error,
          });
          updateState({ isLoading: false });
        });
    },

    getIpConfigurations: hotels => {
      const ipData = {};

      for (const hotel of hotels) {
        const currentWhitelist = hotel?.ipWhitelist
          ? hotel.ipWhitelist.split(',').map(ip => ip.trim())
          : [];
        const originalIps = state.originalWhitelists
          .find(i => i.hotelId === hotel.hotelId)
          ?.list?.map(ip => ip.trim());

        if (!ipData[hotel.hotelId]) {
          ipData[hotel.hotelId] = { newIps: [], revokedIps: [] };
        }

        for (const ip of originalIps || []) {
          if (currentWhitelist && !currentWhitelist.includes(ip.trim())) {
            ipData[hotel.hotelId].revokedIps.push(ip.trim());
          }
        }

        for (const ip of currentWhitelist) {
          if (!originalIps || !originalIps?.includes(ip.trim())) {
            if (ip.length) {
              ipData[hotel.hotelId].newIps.push(ip.trim());
            }
          }
        }
      }

      return ipData;
    },
    loadPaperlessDevices: () => {
      updateState({ isPaperlessDevicesLoaded: true, isLoading: true });
      const hotelId = getSelectedHotel().key;
      API.getPaperlessDevices({ hotelId })
        .then(response => {
          updateState({ paperlessDevices: response, isLoading: false });
        })
        .catch(err => {
          updateState({ isLoading: false });
        });
    },
    onDeletePaperlessDevice: () => {
      updateState({ isLoading: true, deleteDeviceWarning: false });
      API.deletePaperlessDevice({ deviceUUID: state.selectedDeviceUUID })
        .then(response => {
          notification.success({
            message: translate('paperlessPush.deleteSuccess'),
          });
          updateState({ isLoading: false, isPaperlessDevicesLoaded: false });
        })
        .catch(err => {
          notification.error({
            message: translate('paperlessPush.deleteError'),
          });
          updateState({ isLoading: false });
        });
    },
    onDeleteDeviceClicked: deviceUUID => {
      updateState({ selectedDeviceUUID: deviceUUID });
      actions.setShowDeleteDevice(true);
    },
    onEditDeviceClicked: (deviceUUID, selectedDevice) => {
      updateState({
        selectedDeviceUUID: deviceUUID,
        selectedDevice,
        showEditDeviceModal: true,
      });
    },
    setShowDeleteDevice: show => {
      updateState({ deleteDeviceWarning: show });
    },
    setShowDeviceModal: show => {
      updateState({ showEditDeviceModal: show });
    },
    onSaveEditDevice: (newName, paperlessLockCarrusel) => {
      updateState({ isLoading: true });

      API.updatePaperlessDevice({
        oldUUID: state.selectedDeviceUUID,
        newUUID: newName,
        paperlessLockCarrusel,
      })
        .then(response => {
          notification.success({
            message: translate('paperlessPush.editDeviceModal.successUpdate'),
          });

          // Trigger reload of device list to fetch the updated device
          updateState({
            isLoading: false,
            showEditDeviceModal: false,
            isPaperlessDevicesLoaded: false,
          });
        })
        .catch(err => {
          const errorMessage = err?.response?.data?.error || '';

          if (errorMessage.includes('Device name already in use')) {
            notification.error({
              message: translate('paperlessPush.editDeviceModal.errors.nameAlreadyInUse'),
            });
          } else {
            notification.error({
              message: translate('paperlessPush.editDeviceModal.errors.generalError'),
            });
          }

          updateState({ isLoading: false });
        });
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}
